<template>
  <div>
    <NylonOptionGrid preview :embedded="embedded">
      <button
        v-for="option in placements"
        :key="option.name"
        class="nylon-OptionGrid-option"
        :class="{ active: placement === option.name }"
        :title="option.sizeTitle"
        @click.prevent="$emit('placement-change', option.name)"
      >
        <figure>
          <NylonIcon :icon-name="option.iconName" class="icon" view-box="0 0 72 72" />
        </figure>
        <div class="text">
          <span class="heading">{{ option.heading }}</span>
        </div>
      </button>
    </NylonOptionGrid>
    <NylonDimensionFields
      v-if="showAdvanced"
      :geometry="debouncedInput.printGeometry"
      :custom-size="customSize"
      :show-offset="showOffset"
      :offset="debouncedInput.printOffset"
      :disable-offset-y="disableOffsetY"
      :show-offset-x="showOffsetX"
      :offset-range="offsetRange ?? undefined"
      @width-input="$emit('print-width-input', $event)"
      @height-input="$emit('print-height-input', $event)"
      @offset-x-input="$emit('offset-x-input', $event)"
      @offset-y-input="$emit('offset-y-input', $event)"
    />
  </div>
</template>

<script>
import { productTypes, productionMethods, customPlacements, nudgesVerticallyOnly } from "@/lib/product-creator.js";
import NylonOptionGrid from "@/nylon/components/NylonOptionGrid.vue";
import NylonDimensionFields from "@/nylon/components/NylonDimensionFields.vue";
/**
 * Nylon — PlacementOptions
 *
 * The Design Placement panel body in the submission wizard's BUILDER
 * step + the account edit-product step 2. Two parts:
 *   1. A button grid of placement choices (Max Centered / Large /
 *      Medium / Small / Pocket / Custom — the set varies by product
 *      type + decoration method, driven by the `placements` array).
 *   2. An advanced custom-dimension block (Width / Height inputs,
 *      plus Offset X / Y for APPAREL non-embroidery, Offset Y for HAT / BEANIE) shown only when
 *      the selected placement is a custom variant.
 *
 * Both consumers (`create-a-product.vue`, `account/edit-product.vue`)
 * drive this from the shared `ProductCreatorMixin`, so the prop +
 * emit surface is identical across them. The component is mounted
 * inside a `<NylonOptionPanel>` by the caller; the panel wrapper and
 * any footer (e.g. the PHONECASE/DRINKWARE "don't scale artwork"
 * checkbox) stay at the call site.
 *
 * The dimension inputs live in `<NylonDimensionFields>`, which v-models
 * `debouncedInput.printGeometry` / `.printOffset` in place (a shared
 * reactive object passed by reference) for instant display and emits
 * the raw input events; this component relays those events upward so
 * the parent's debounced mixin handler can clamp + write the real
 * geometry.
 *
 * The placement button grid now renders through `<NylonOptionGrid
 * preview>` (the consolidated option-grid primitive); the `--preview`
 * modifier gives the large outline icons. The advanced custom-dimension
 * block renders through `<NylonDimensionFields>` (shared with STICKER
 * custom-size in ColorStylePanel); the Offset X / Y subgroup is gated
 * via the `showOffset` prop (APPAREL non-embroidery, and HAT / BEANIE
 * with Offset Y only).
 *
 * Props:
 *   placements       (Array)  — placement option objects { name, iconName, heading, sizeTitle }
 *   placement        (String) — currently-selected placement name
 *   debouncedInput   (Object) — the mixin's debounced input; NylonDimensionFields v-models
 *                               its printGeometry + printOffset
 *   customSize       (Object) — { min: {width,height}, max: {width,height}, step } for the inputs
 *   productType      (String) — current product type (gates the Offset X/Y subgroup; HAT / BEANIE show Y only)
 *   productionMethod (String) — current decoration method (gates the Offset X/Y subgroup)
 *   embedded         (Boolean)— narrow account-column context (edit-product): grid goes
 *                               4-up → 3-up @lg via NylonOptionGrid's --embedded modifier
 *   disableOffsetY   (Boolean)— disable the Offset Y input (the back-neck placement
 *                               makes a Y offset moot)
 *   offsetRange      (Object) — { min: {x,y}, max: {x,y}, step } for the offset inputs
 *                               (the mixin's offsetRange); NylonDimensionFields' ±1in when absent
 *
 * Emits:
 *   placement-change (name)  — a placement button clicked
 *   print-width-input ($event), print-height-input ($event),
 *   offset-x-input ($event),  offset-y-input ($event) — raw input events
 *     for the parent's debounced handlers
 */
export default {
  name: "NylonPlacementOptions",
  components: { NylonOptionGrid, NylonDimensionFields },
  props: {
    placements: { type: Array, required: true },
    placement: { type: String, default: "" },
    debouncedInput: { type: Object, required: true },
    customSize: { type: Object, required: true },
    productType: { type: String, default: "" },
    productionMethod: { type: String, default: "" },
    embedded: { type: Boolean, default: false },
    // The back-neck placement makes a Y offset moot — the caller disables
    // it while that placement is selected.
    disableOffsetY: { type: Boolean, default: false },
    offsetRange: { type: Object, default: null },
  },
  emits: ["placement-change", "print-width-input", "print-height-input", "offset-x-input", "offset-y-input"],
  computed: {
    showAdvanced() {
      return customPlacements.includes(this.placement);
    },
    // Apparel other than embroidery, and hats and beanies (a vertical nudge, PRO-1011).
    showOffset() {
      return (this.productType === productTypes.APPAREL && this.productionMethod !== productionMethods.EMBROIDERY) || nudgesVerticallyOnly(this.productType);
    },
    showOffsetX() {
      return !nudgesVerticallyOnly(this.productType);
    },
  },
};
</script>
